'use client';

import VideoComponent from '@/components/hero/VideoComponent';
import { useSizeModal } from '@/helpers/size_guide_modal';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import { SectionItem } from '@/types/product_page/productType';
import { getThemeClass } from '@/helpers/theme';
import { generateDirectusSrc, generateDirectusSrcSet } from '@/helpers/image';

const Advanced = ({ data }: { data: SectionItem }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [active, setActive] = useState(true);
  const { isSizeModalOpen } = useSizeModal();
  const firstButtonRef = useRef<any>(null);
  const lineRef = useRef<any>(null);
  const containerRef = useRef<any>(null);
  const theme = data?.theme || 'dark';

  const handleButtonClick = (index: number, event: any) => {
    if (selectedIndex !== index) {
      setActive(false);
    }
    const timeout = setTimeout(() => {
      setSelectedIndex(index);
    }, 300);

    const lineElement = lineRef.current;
    if (lineElement) {
      const buttonRect = event.target.getBoundingClientRect();
      const lineLeft =
        buttonRect.left -
        lineElement.parentElement.getBoundingClientRect().left;
      lineElement.style.transform = `translateX(${lineLeft}px)`;
      lineElement.style.width = `${buttonRect.width}px`;
    }

    const button = event.currentTarget;
    if (containerRef.current && button) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();

      const scrollLeft =
        buttonRect.left -
        containerRect.left -
        containerRect.width / 2 +
        buttonRect.width / 2;
      containerRef.current.scrollBy({
        left: scrollLeft,
        top: 0,
        behavior: 'smooth',
      });
    }
    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    setActive(true);
  }, [selectedIndex]);

  useEffect(() => {
    if (firstButtonRef.current) {
      firstButtonRef.current.click();
    }
    setActive(true);
  }, []);

  return (
    <div
      className={`w-full flex items-center ${getThemeClass(theme, 'background')} justify-center py-8 md:py-14 px-4`}
      id={data?.heading}
    >
      <div className={'max-w-7xl w-full flex flex-col items-center'}>
        {/* Section heading */}
        <h1
          className={`text-3xl lg:text-4.5xl ${getThemeClass(theme, 'heading')} font-bold text-center mb-8`}
        >
          {data?.heading}
        </h1>

        {data?.description && (
          <div className={`w-full mb-8`}>
            <p
              className={`font-medium text-[16px] ${getThemeClass(theme, 'text')} font-proxima_font text-center max-w-screen-md mx-auto`}
            >
              {data?.description}
            </p>
          </div>
        )}

        {/* //Images section */}
        <div
          className={cn(
            `relative aspect-[4/3] lg:aspect-auto sm:h-96 lg:h-[650px] w-full rounded-[10px]  overflow-hidden`,
            isSizeModalOpen && 'z-[-1] '
          )}
        >
          {data.tabs?.map((media, index) => {
            return !media?.tab_banner_translations_id?.has_video ? (
              <picture key={index}>
                {media.tab_banner_translations_id?.mobile_bg_image && (
                  <source
                    media="(max-width: 639px)"
                    srcSet={generateDirectusSrcSet(
                      media.tab_banner_translations_id.mobile_bg_image.id
                    )}
                    sizes="(max-width: 639px) 100vw, 640px"
                  />
                )}
                <source
                  media="(min-width: 640px)"
                  srcSet={generateDirectusSrcSet(
                    media?.tab_banner_translations_id?.image?.id
                  )}
                  sizes="(min-width: 640px) 100vw, 1200px"
                />
                <img
                  alt={media?.tab_banner_translations_id?.image?.title}
                  src={generateDirectusSrc(
                    media.tab_banner_translations_id.image?.id,
                    1200
                  )}
                  srcSet={generateDirectusSrcSet(
                    media.tab_banner_translations_id.image?.id
                  )}
                  sizes="(max-width: 768px) 100vw, 1200px"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover bg-cover bg-center transition-all duration-300 opacity-0"
                  style={{
                    opacity: index === selectedIndex ? 1 : 0,
                  }}
                />
              </picture>
            ) : media?.tab_banner_translations_id?.has_video &&
              index === selectedIndex ? (
              <div
                key={
                  media?.tab_banner_translations_id?.video &&
                  typeof media.tab_banner_translations_id.video === 'object'
                    ? media.tab_banner_translations_id.video.mux_playback_id ??
                      media.tab_banner_translations_id.video.id
                    : index
                }
                className="absolute w-full h-full object-cover bg-cover bg-center"
              >
                <VideoComponent
                  key={
                    media?.tab_banner_translations_id?.video &&
                    typeof media.tab_banner_translations_id.video === 'object'
                      ? media.tab_banner_translations_id.video
                          .mux_playback_id ??
                        media.tab_banner_translations_id.video.id
                      : (media?.tab_banner_translations_id?.video as string) ??
                        index
                  }
                  file={
                    media?.tab_banner_translations_id?.video &&
                    typeof media.tab_banner_translations_id.video === 'object'
                      ? media.tab_banner_translations_id.video.id
                      : ((media?.tab_banner_translations_id?.video ??
                          '') as string)
                  }
                  mobile_file={
                    media?.tab_banner_translations_id?.mobile_video ?? null
                  }
                  loop={media?.tab_banner_translations_id?.loop}
                  youtube_url={
                    media?.tab_banner_translations_id?.youtube_url ?? null
                  }
                  autoplay={media?.tab_banner_translations_id?.autoplay}
                  thumbnail={media?.tab_banner_translations_id?.thumbnail}
                  mux_playback_id={
                    media?.tab_banner_translations_id?.video &&
                    typeof media.tab_banner_translations_id.video === 'object'
                      ? media.tab_banner_translations_id.video.mux_playback_id
                      : null
                  }
                />
              </div>
            ) : null;
          })}
        </div>
        <div className="w-full overflow-hidden py-4">
          {data.tabs && data.tabs.length > 1 && (
            <div className="w-full flex overflow-auto no-scrollbar">
              <div
                ref={containerRef}
                className="flex font-proxima_font gap-0 items-center md:justify-center overflow-x-auto no-scrollbar flex-shrink-0 w-full transition-all duration-1000"
              >
                {/* //button highlighted horizonal line */}
                <div
                  className={`relative gap-8 border-b  border-slate-500  border-opacity-50 flex items-center justify-between flex-shrink-0 md:w-auto mx-auto`}
                >
                  <div
                    ref={lineRef}
                    className={`absolute -bottom-[1.2px] h-[3px] rounded-full`}
                    style={{
                      transition:
                        'width 0.3s ease-in-out, transform 0.3s ease-in-out',
                      backgroundColor: theme === 'dark' ? '#FFFFFF' : '#000000',
                    }}
                  ></div>

                  {data.tabs?.map((button, index) => (
                    <div
                      key={index}
                      ref={index === 0 ? firstButtonRef : null}
                      onClick={(event) => handleButtonClick(index, event)}
                      className={`py-2 font-semibold transition-color duration-200 ${getThemeClass(theme, 'heading')} ${
                        selectedIndex === index ? `opacity-100` : 'opacity-50'
                      } cursor-pointer text-sm lg:text-base whitespace-nowrap`}
                    >
                      {button.tab_banner_translations_id.title}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {data.tabs &&
            data.tabs[selectedIndex]?.tab_banner_translations_id.sub_title && (
              <div className={`w-full mt-6 `}>
                <p
                  className={`font-medium transition-all duration-500 text-[16px] font-proxima_font ${getThemeClass(theme, 'text')} text-center max-w-7xl mx-auto ${active ? 'opacity-80' : 'opacity-0'}`}
                >
                  {data.tabs &&
                    data.tabs[selectedIndex]?.tab_banner_translations_id
                      .sub_title}
                </p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default Advanced;
